import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { authApi, type RoleName, type User } from '~/lib/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
}

interface AuthContextType extends AuthState {
  login: (oktaToken: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  hasRole: (role: RoleName) => boolean
  hasMinRole: (minRole: RoleName) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

const ROLE_LEVELS: Record<RoleName, number> = {
  user: 1,
  operator: 2,
  admin: 3,
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    token: localStorage.getItem('auth_token'),
  })

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setState((prev) => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const { user } = await authApi.me()
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          token,
        })
      } catch {
        // Token is invalid or expired
        localStorage.removeItem('auth_token')
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          token: null,
        })
      }
    }

    checkAuth()
  }, [])

  const login = async (oktaToken: string) => {
    const response = await authApi.login(oktaToken)
    localStorage.setItem('auth_token', response.token)
    setState({
      user: response.user,
      isAuthenticated: true,
      isLoading: false,
      token: response.token,
    })
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignore errors during logout
    }
    localStorage.removeItem('auth_token')
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
    })
  }

  const refreshToken = async () => {
    try {
      const response = await authApi.refresh()
      localStorage.setItem('auth_token', response.token)
      setState((prev) => ({ ...prev, token: response.token }))
    } catch {
      await logout()
    }
  }

  const hasRole = (role: RoleName): boolean => {
    return state.user?.role === role
  }

  const hasMinRole = (minRole: RoleName): boolean => {
    if (!state.user) return false
    return ROLE_LEVELS[state.user.role] >= ROLE_LEVELS[minRole]
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshToken,
        hasRole,
        hasMinRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
